import type { CustomField } from '../api/types';
import type { ConditionalRule } from '../config';
import { isValueTruthy, type CustomFieldValues } from './customFieldValues';

export interface VisibilityResult {
  visibleCodes: Set<string>;
  /** true when the rules were unusable and every field was shown instead */
  degraded: boolean;
}

/**
 * Which custom fields are visible right now. Base visibility comes from `field.visible`; rules
 * then hide dependents whose controlling field is not in the required state. A rule whose
 * controlling field does not exist (the client hasn't created it yet) is ignored, so its
 * dependents stay unconditional. Any exception degrades to "show everything".
 */
export function computeVisibility(
  fields: CustomField[],
  values: CustomFieldValues,
  rules: ConditionalRule[],
): VisibilityResult {
  const base = new Set<string>();
  for (const f of fields) if (f.visible !== false && f.code) base.add(f.code);
  if (!rules.length) return { visibleCodes: base, degraded: false };

  try {
    const byCode = new Map<string, CustomField>();
    for (const f of fields) if (f.code) byCode.set(f.code, f);

    const rulesByDependent = new Map<string, ConditionalRule[]>();
    for (const rule of rules) {
      if (!rule?.when?.code || !Array.isArray(rule.show)) throw new Error('malformed conditional rule');
      if (!byCode.has(rule.when.code)) continue;
      for (const code of rule.show) {
        const list = rulesByDependent.get(code) ?? [];
        list.push(rule);
        rulesByDependent.set(code, list);
      }
    }

    const visible = new Set(base);
    for (const [code, list] of rulesByDependent) {
      if (!base.has(code)) continue;
      const shown = list.some((rule) => {
        const controller = byCode.get(rule.when.code);
        const checked = typeof controller?.id === 'number' && isValueTruthy(values[controller.id]);
        return rule.when.is === 'unchecked' ? !checked : checked;
      });
      if (!shown) visible.delete(code);
    }
    return { visibleCodes: visible, degraded: false };
  } catch (err) {
    console.warn('[cbf] conditional rules failed; rendering all custom fields', err);
    return { visibleCodes: base, degraded: true };
  }
}

export function isFieldVisible(field: CustomField, result: VisibilityResult): boolean {
  return !!field.code && result.visibleCodes.has(field.code);
}
